import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  Users,
  Star,
  TrendingUp,
  ChevronRight,
  MapPin,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Loading } from '../../components/ui/Loading';

interface ScheduledClass {
  id: string;
  name: string;
  date: string;
  time: string;
  endTime?: string;
  enrolled: Record<string, any>;
  maxCapacity: number;
  location?: string;
  classTypeId?: string;
  color?: string;
}

interface InstructorData {
  id: string;
  firstName: string;
  lastName: string;
  rating: number;
  totalClasses: number;
  totalStudents: number;
  avatar?: string;
}

const InstructorDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { businessId } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [todayClasses, setTodayClasses] = useState<ScheduledClass[]>([]);
  const [upcomingClasses, setUpcomingClasses] = useState<ScheduledClass[]>([]);
  const [instructorData, setInstructorData] = useState<InstructorData | null>(null);
  const [monthlyStats, setMonthlyStats] = useState({
    classesThisMonth: 0,
    studentsThisMonth: 0,
    averageAttendance: 0
  });

  useEffect(() => {
    if (businessId && currentUser) {
      loadInstructorData();
    }
  }, [businessId, currentUser]);

  const loadInstructorData = async () => {
    if (!businessId || !currentUser) return;

    try {
      setLoading(true);

      // Buscar el instructor vinculado a este usuario
      const instructorsQuery = query(
        collection(db, 'instructors'),
        where('businessId', '==', businessId),
        where('userId', '==', currentUser.id)
      );
      const instructorsSnapshot = await getDocs(instructorsQuery);

      if (instructorsSnapshot.empty) {
        console.warn('No se encontró instructor vinculado a este usuario');
        setLoading(false);
        return;
      }

      const instructorDoc = instructorsSnapshot.docs[0];
      const instructor = {
        id: instructorDoc.id,
        ...instructorDoc.data()
      } as InstructorData;
      setInstructorData(instructor);

      // Cargar clases de hoy y próximas
      const today = new Date().toISOString().split('T')[0];
      const classesQuery = query(
        collection(db, 'classes'),
        where('businessId', '==', businessId),
        where('instructorId', '==', instructor.id),
        where('isActive', '==', true)
      );
      const classesSnapshot = await getDocs(classesQuery);

      const allClasses = classesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as ScheduledClass[];

      // Filtrar clases de hoy
      const todaysClasses = allClasses
        .filter(c => c.date === today)
        .sort((a, b) => a.time.localeCompare(b.time));
      setTodayClasses(todaysClasses);

      // Filtrar próximas clases (siguientes 7 días, excluyendo hoy)
      const nextWeek = new Date();
      nextWeek.setDate(nextWeek.getDate() + 7);
      const upcoming = allClasses
        .filter(c => c.date > today && c.date <= nextWeek.toISOString().split('T')[0])
        .sort((a, b) => {
          if (a.date === b.date) return a.time.localeCompare(b.time);
          return a.date.localeCompare(b.date);
        })
        .slice(0, 5);
      setUpcomingClasses(upcoming);

      // Calcular estadísticas del mes
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      const monthClasses = allClasses.filter(c => c.date >= startOfMonth.toISOString().split('T')[0]);
      const totalEnrolled = monthClasses.reduce((sum, c) => sum + Object.keys(c.enrolled || {}).length, 0);
      const totalCapacity = monthClasses.reduce((sum, c) => sum + c.maxCapacity, 0);

      setMonthlyStats({
        classesThisMonth: monthClasses.length,
        studentsThisMonth: totalEnrolled,
        averageAttendance: totalCapacity > 0 ? Math.round((totalEnrolled / totalCapacity) * 100) : 0
      });

    } catch (error) {
      console.error('Error cargando datos del instructor:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getNextClass = () => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    
    // Primero buscar en clases de hoy que aún no han pasado
    const nextToday = todayClasses.find(c => c.time > currentTime);
    if (nextToday) return { ...nextToday, isToday: true };

    // Si no hay más hoy, devolver la primera de próximas
    if (upcomingClasses.length > 0) {
      return { ...upcomingClasses[0], isToday: false };
    }

    return null;
  };

  const nextClass = getNextClass();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  if (!instructorData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Cuenta no vinculada</h2>
          <p className="text-gray-400">
            Tu cuenta no está vinculada a un perfil de instructor.
            <br />
            Contacta con el administrador para vincular tu cuenta.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header de bienvenida */}
      <div className="bg-gradient-to-r from-primary-600/20 to-primary-900/20 rounded-2xl p-6 border border-primary-500/20">
        <div className="flex items-center gap-4">
          {instructorData.avatar ? (
            <img
              src={instructorData.avatar}
              alt={instructorData.firstName}
              className="w-16 h-16 rounded-full object-cover border-2 border-primary-500"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-primary-500/30 flex items-center justify-center border-2 border-primary-500">
              <span className="text-2xl font-bold text-primary-300">
                {instructorData.firstName?.charAt(0)}
              </span>
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-white">
              ¡Hola, {instructorData.firstName}!
            </h1>
            <p className="text-gray-400">
              {todayClasses.length > 0 
                ? `Tienes ${todayClasses.length} clase${todayClasses.length > 1 ? 's' : ''} hoy`
                : 'No tienes clases programadas para hoy'}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-amber-500/20 px-3 py-1 rounded-full">
            <Star className="w-4 h-4 text-amber-400" />
            <span className="text-amber-400 font-semibold">
              {(instructorData.rating || 0).toFixed(1)}
            </span>
          </div>
        </div>
      </div>

      {/* Próxima clase destacada */}
      {nextClass && (
        <Card className="bg-gradient-to-br from-primary-500/10 to-primary-600/5 border-primary-500/30">
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-primary-400 uppercase tracking-wide">
                Próxima Clase
              </h2>
              {nextClass.isToday && (
                <span className="px-2 py-0.5 bg-primary-500 text-white text-xs font-medium rounded-full">
                  HOY
                </span>
              )}
            </div>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-white mb-2">{nextClass.name}</h3>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{formatTime(nextClass.time)}</span>
                    {!nextClass.isToday && (
                      <>
                        <span className="text-gray-600">•</span>
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(nextClass.date)}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <Users className="w-4 h-4" />
                    <span>{Object.keys(nextClass.enrolled || {}).length}/{nextClass.maxCapacity} inscritos</span>
                  </div>
                  {nextClass.location && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4" />
                      <span>{nextClass.location}</span>
                    </div>
                  )}
                </div>
              </div>
              <Button
                onClick={() => navigate(`/instructor/class/${nextClass.id}`)}
                className="flex items-center gap-2"
              >
                Ver detalles
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Estadísticas del mes */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-primary-400">{monthlyStats.classesThisMonth}</div>
          <div className="text-sm text-gray-500 mt-1">Clases este mes</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-green-400">{monthlyStats.studentsThisMonth}</div>
          <div className="text-sm text-gray-500 mt-1">Alumnos atendidos</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-3xl font-bold text-amber-400">{monthlyStats.averageAttendance}%</div>
          <div className="text-sm text-gray-500 mt-1">Asistencia media</div>
        </Card>
      </div>

      {/* Clases de hoy */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Clases de hoy</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/instructor/classes')}>
            Ver todas
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>

        {todayClasses.length === 0 ? (
          <Card className="p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No tienes clases programadas para hoy</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {todayClasses.map((classItem) => {
              const now = new Date();
              const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
              const isPast = classItem.time < currentTime;
              const enrolledCount = Object.keys(classItem.enrolled || {}).length;

              return (
                <Card
                  key={classItem.id}
                  className={`p-4 cursor-pointer transition-all hover:border-primary-500/50 ${
                    isPast ? 'opacity-60' : ''
                  }`}
                  onClick={() => navigate(`/instructor/class/${classItem.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        isPast ? 'bg-gray-700' : 'bg-primary-500/20'
                      }`}>
                        <Clock className={`w-5 h-5 ${isPast ? 'text-gray-500' : 'text-primary-400'}`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-white">{classItem.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <span>{formatTime(classItem.time)}</span>
                          {classItem.location && (
                            <>
                              <span className="text-gray-600">•</span>
                              <span>{classItem.location}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        enrolledCount >= classItem.maxCapacity
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {enrolledCount}/{classItem.maxCapacity}
                      </div>
                      {isPast ? (
                        <CheckCircle className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Próximas clases */}
      {upcomingClasses.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Próximas clases</h2>
          </div>
          <div className="space-y-2">
            {upcomingClasses.map((classItem) => {
              const enrolledCount = Object.keys(classItem.enrolled || {}).length;
              
              return (
                <Card
                  key={classItem.id}
                  className="p-3 cursor-pointer transition-all hover:border-primary-500/50"
                  onClick={() => navigate(`/instructor/class/${classItem.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-white">{classItem.name}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(classItem.date)}</span>
                        <span className="text-gray-600">•</span>
                        <Clock className="w-3 h-3" />
                        <span>{formatTime(classItem.time)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-400">
                        {enrolledCount}/{classItem.maxCapacity}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-500" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default InstructorDashboard;



















